import { create } from "zustand"
import type {
    AvatarModel,
    AvatarPolarity,
    QuaternionTuple,
    Vector3Tuple,
} from "@domo/shared"

// Sprint 3/6 — Fases del flujo de encarnación. Vive solo en cliente
// porque las animaciones son pintura local; el server solo distingue
// entre incarnated true/false.
//   "void"       → fondo negro absoluto, no hay Domo, orbe + UI visible
//   "descending" → SPAWN_EVENT recibido, haz de luz cayendo
//   "materializing" → haz tocó pad, sparkles + emerge avatar
//   "incarnated" → flujo normal del Domo (movimiento habilitado)
//   "ascending" → Tripulante pisó portal de reconfiguración. El cuerpo
//     se transmuta en orbe de luz dorada que asciende al cielo. Al
//     final se emite RECONFIGURE_REQUEST y el server responde con
//     VOID_RETURN, llevando la fase a "void" para elegir nueva firma.
export type IncarnationPhase =
    | "void"
    | "descending"
    | "materializing"
    | "incarnated"
    | "ascending"

// Sprint 6 — Sub-estado del void.
//   "checking" → recién conectados, esperando si el server auto-encarna
//                 (firma persistida) o no. NO renderizamos la intro
//                 narrativa todavía: si llega SPAWN_EVENT auto, la
//                 fase pasa a "descending" sin que el Tripulante vea
//                 el orbe parpadeando.
//   "intro"    → mensaje narrativo del Estado Cero (orbe + texto).
//                 Aparece tras un timeout corto si no hubo auto, y
//                 también al volver al void via reconfiguración.
//   "selector" → modal glassmorphism de elección de firma.
export type VoidStage = "checking" | "intro" | "selector"

// 2026-05-05 v6 — Modo de cámara. "thirdPerson" sigue al avatar
// orbitando alrededor (default). "firstPerson" planta la cámara en
// la cabeza del avatar mirando hacia donde mira el cuerpo — el HUD
// puede alternar con un botón.
export type CameraMode = "thirdPerson" | "firstPerson"

// 2026-05-05 v7 — Orientación libre de la primera persona. cameraYaw
// es la rotación horizontal acumulada (radianes) que dispara el drag
// del Tripulante en FP, y que el avatar copia en idle (gira hacia
// donde mira la cámara). Pitch es la inclinación arriba/abajo, clamp
// suave para evitar voltearse de cabeza. Cuando el avatar camina,
// LocalAvatar reescribe cameraYaw al ángulo del lookAt para que la
// cámara hereda automáticamente la dirección del cuerpo. La convención
// es: yaw=0 mira al +Z (lookAt natural de Object3D después de un
// lookAt al norte), yaw positivo gira CCW visto desde arriba.
const FP_PITCH_MIN = -Math.PI / 2.5
const FP_PITCH_MAX = Math.PI / 3

function clampPitch(p: number): number {
    return Math.max(FP_PITCH_MIN, Math.min(FP_PITCH_MAX, p))
}

interface PlayerState {
    id: string | null
    displayName: string
    position: Vector3Tuple
    target: Vector3Tuple | null
    rotation: QuaternionTuple
    siliconIndex: number
    isAnchored: boolean
    // Sprint 3/5/6 — Encarnación
    model: AvatarModel | null
    polarity: AvatarPolarity | null
    isIncarnated: boolean
    phase: IncarnationPhase
    voidStage: VoidStage
    descentStartedAt: number | null // performance.now() del comienzo del haz
    descentMs: number // duración solo del haz cayendo
    materializeMs: number // duración solo del shimmer + emerge avatar
    descentTotalMs: number // descentMs + materializeMs (cache)
    spawnPad: Vector3Tuple // dónde aterriza este Tripulante (lo da el server)
    autoIncarnated: boolean // true si vino por auto-encarnación, no por selector
    // 2026-05-05 v6 — Cámara primera vs tercera persona
    cameraMode: CameraMode
    // 2026-05-05 v7 — Orientación libre de la primera persona.
    cameraYaw: number
    cameraPitch: number
    // 2026-05-06 — Bandera de proximidad al portal de reconfiguración
    // (anillo dorado lateral). El sensor en Canvas la actualiza por
    // frame; el overlay UI lee la bandera para mostrar el prompt.
    isOnElectionPortal: boolean
    // 2026-05-06 — Timestamp del inicio de la animación de ascensión
    // (avatar se transmuta en orbe dorado y sube al cielo). LocalAvatar
    // y AscentOrb leen esto para calcular progresión por frame.
    ascentStartedAt: number | null
    ascentDurationMs: number

    setIdentity: (id: string, displayName: string) => void
    setPosition: (p: Vector3Tuple) => void
    setTarget: (t: Vector3Tuple | null) => void
    setRotation: (r: QuaternionTuple) => void
    setSiliconIndex: (n: number) => void
    setAnchored: (a: boolean) => void
    // Sprint 3/5 — Encarnación
    setModel: (m: AvatarModel | null) => void
    setPolarity: (p: AvatarPolarity | null) => void
    setCameraMode: (m: CameraMode) => void
    toggleCameraMode: () => void
    setCameraYaw: (yaw: number) => void
    setCameraPitch: (pitch: number) => void
    setCameraOrientation: (yaw: number, pitch: number) => void
    setOnElectionPortal: (v: boolean) => void
    /** 2026-05-06 — Inicia la animación local de ascensión. Cambia
     *  phase a "ascending" y arranca el timestamp. NO emite nada al
     *  server (eso lo hace ElectionPortalPrompt al final del timer
     *  para sincronizar la animación con el VOID_RETURN del server). */
    beginAscent: (durationMs: number) => void
    beginDescent: (
        model: AvatarModel,
        polarity: AvatarPolarity,
        pad: Vector3Tuple,
        descentMs: number,
        materializeMs: number,
        auto?: boolean
    ) => void
    advanceToMaterializing: () => void
    completeIncarnation: () => void
    /** Sprint 5 — Cámara de Reconfiguración. Vuelve la fase a "void"
     *  manteniendo identidad/silicon/posición; limpia firma + animación. */
    returnToVoid: () => void
    /** Sprint 6 — Cierra la intro narrativa del Estado Cero y abre el
     *  selector de firma. Disparado por el botón "CONTINUAR" del overlay. */
    advanceVoidStage: () => void
    reset: () => void
}

const INITIAL = {
    id: null,
    displayName: "Tripulante",
    position: [0, 0, 0] as Vector3Tuple,
    target: null,
    rotation: [0, 0, 0, 1] as QuaternionTuple,
    siliconIndex: 50,
    isAnchored: false,
    model: null as AvatarModel | null,
    polarity: null as AvatarPolarity | null,
    isIncarnated: false,
    phase: "void" as IncarnationPhase,
    voidStage: "checking" as VoidStage,
    descentStartedAt: null as number | null,
    descentMs: 0,
    materializeMs: 0,
    descentTotalMs: 0,
    spawnPad: [0, 0, 0] as Vector3Tuple,
    autoIncarnated: false,
    cameraMode: "thirdPerson" as CameraMode,
    cameraYaw: 0,
    cameraPitch: 0,
    isOnElectionPortal: false,
    ascentStartedAt: null as number | null,
    ascentDurationMs: 0,
}

export const usePlayerStore = create<PlayerState>((set) => ({
    ...INITIAL,
    setIdentity: (id, displayName) => set({ id, displayName }),
    setPosition: (position) => set({ position }),
    setTarget: (target) => set({ target }),
    setRotation: (rotation) => set({ rotation }),
    setSiliconIndex: (siliconIndex) =>
        set({ siliconIndex: Math.max(0, Math.min(100, siliconIndex)) }),
    setAnchored: (isAnchored) => set({ isAnchored }),
    setModel: (model) => set({ model }),
    setPolarity: (polarity) => set({ polarity }),
    setCameraMode: (cameraMode) => set({ cameraMode }),
    toggleCameraMode: () =>
        set((s) => ({
            cameraMode:
                s.cameraMode === "firstPerson"
                    ? "thirdPerson"
                    : "firstPerson",
        })),
    setCameraYaw: (yaw) => set({ cameraYaw: yaw }),
    setCameraPitch: (pitch) => set({ cameraPitch: clampPitch(pitch) }),
    setCameraOrientation: (yaw, pitch) =>
        set({ cameraYaw: yaw, cameraPitch: clampPitch(pitch) }),
    setOnElectionPortal: (v) =>
        set((s) =>
            s.isOnElectionPortal === v ? s : { isOnElectionPortal: v }
        ),
    beginAscent: (durationMs) =>
        set({
            phase: "ascending",
            ascentStartedAt: performance.now(),
            ascentDurationMs: durationMs,
            target: null,
            isOnElectionPortal: false,
        }),
    beginDescent: (model, polarity, pad, descentMs, materializeMs, auto) =>
        set({
            model,
            polarity,
            spawnPad: pad,
            position: pad,
            phase: "descending",
            descentStartedAt: performance.now(),
            descentMs,
            materializeMs,
            descentTotalMs: descentMs + materializeMs,
            isIncarnated: false,
            autoIncarnated: Boolean(auto),
        }),
    advanceToMaterializing: () => set({ phase: "materializing" }),
    completeIncarnation: () =>
        set({
            phase: "incarnated",
            isIncarnated: true,
        }),
    returnToVoid: () =>
        set({
            phase: "void",
            // Sprint 6 v2 — reconfiguración pasa por la intro otra vez:
            // el Tripulante "deja su forma y vuelve a ser consciencia
            // sin cuerpo" antes de elegir un avatar nuevo. Decisión
            // explícita: la intro es el rito de paso, no se salta.
            voidStage: "intro",
            isIncarnated: false,
            model: null,
            polarity: null,
            descentStartedAt: null,
            descentMs: 0,
            materializeMs: 0,
            descentTotalMs: 0,
            target: null,
            isAnchored: false,
            autoIncarnated: false,
            // 2026-05-06 — Limpiar el timestamp de ascensión para que
            // si el Tripulante reincarna y vuelve al portal, la
            // animación arranque limpia desde el inicio.
            ascentStartedAt: null,
            ascentDurationMs: 0,
        }),
    advanceVoidStage: () => set({ voidStage: "selector" }),
    reset: () => set(INITIAL),
}))
